import { SdkContext } from '../internal'

export type SignupRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED'

export interface SignupRequestAnswer {
  fieldId: string
  key: string
  label: string
  type: string
  value: unknown
}

/** A request to join (F-150): never a member until the team approves it. */
export interface SignupRequest {
  id: string
  fullName: string
  email: string
  language: string | null
  phone: string | null
  status: SignupRequestStatus
  reason: string | null
  createdAt: string
  expiresAt: string | null
  decidedAt: string | null
  memberId: string | null
  answers: SignupRequestAnswer[]
}

export interface SignupRequestPage {
  items: SignupRequest[]
  pagination?: { total: number; page: number; limit: number; totalPages: number }
}

/** Per id: `approved` / `rejected`, or why it was skipped. */
export interface SignupDecision {
  results: Array<{ id: string; status: 'approved' | 'rejected' | 'alreadyMember' | 'alreadyDecided' | 'notFound' | 'failed'; memberId?: string }>
  summary: { total: number; approved?: number; rejected?: number; skipped: number }
}

/** The most ids one approve or reject call takes (the API's batch limit). */
export const SIGNUP_DECISION_BATCH_MAX = 100
/** The longest refusal reason the API keeps. */
export const SIGNUP_REASON_MAX = 1000

/**
 * F-150: the public request to join a community that approves signups, and
 * the team's queue (owner and admin; the API answers 403 to anyone else).
 */
export class SignupRequestsResource {
  constructor(private ctx: SdkContext) {}

  async list(params: { status?: SignupRequestStatus; page?: number; limit?: number } = {}): Promise<SignupRequestPage> {
    const r = await this.ctx.call<SignupRequestPage | SignupRequest[]>({ httpMethod: 'GET', path: '/signup-requests' }, { query: params })
    return Array.isArray(r) ? { items: r } : { items: r?.items ?? [], pagination: r?.pagination }
  }

  async approve(ids: string[]): Promise<SignupDecision> {
    return this.ctx.call<SignupDecision>({ httpMethod: 'POST', path: '/signup-requests/approve', hasBody: true }, { body: { ids } })
  }

  async reject(ids: string[], reason?: string): Promise<SignupDecision> {
    return this.ctx.call<SignupDecision>({ httpMethod: 'POST', path: '/signup-requests/reject', hasBody: true }, { body: { ids, ...(reason ? { reason } : {}) } })
  }
}
